import React, { useEffect, useRef, useState } from "react";
import { Camera, CheckCircle2, ScanLine } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

export default function MobileQrScanner() {
  const location = useLocation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanTimerRef = useRef(null);
  const [status, setStatus] = useState("");
  const [approving, setApproving] = useState(false);
  const params = new URLSearchParams(location.search);
  const challengeId = params.get("challenge") || "";
  const approvalToken = params.get("approval") || "";

  const stopCamera = () => {
    clearInterval(scanTimerRef.current);
    scanTimerRef.current = null;
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  };

  useEffect(() => stopCamera, []);

  const approve = async () => {
    if (!challengeId || !approvalToken) {
      setStatus("Scan a valid Nexovate login QR first.");
      return;
    }
    setApproving(true);
    setStatus("Approving desktop login...");
    try {
      const loginToken = localStorage.getItem("ct_login_token");
      if (!loginToken) {
        throw new Error("Login to the Nexovate mobile app before approving");
      }
      const response = await fetch(`${API_BASE}/api/qr-login/approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${loginToken}`,
        },
        body: JSON.stringify({ challengeId, approvalToken }),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Approval failed");
      setStatus(`Approved for ${data.user?.username}. Continue on the desktop.`);
    } catch (error) {
      setStatus(error.message);
    } finally {
      setApproving(false);
    }
  };

  const startCamera = async () => {
    setStatus("");
    if (!("BarcodeDetector" in window)) {
      setStatus("This browser scanner is unavailable. Use your phone Camera app to scan the QR.");
      return;
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
      scanTimerRef.current = setInterval(async () => {
        try {
          const codes = await detector.detect(videoRef.current);
          const value = codes?.[0]?.rawValue;
          if (!value) return;
          const parsed = new URL(value, window.location.origin);

          if (
            parsed.pathname !== "/scan-approve" ||
            !parsed.searchParams.get("challenge") ||
            !parsed.searchParams.get("approval")
          ) {
            setStatus("This is not a Nexovate login QR.");
            return;
          }
          stopCamera();
          navigate(`${parsed.pathname}${parsed.search}`, { replace: true });
        } catch {
          // Continue scanning the next camera frame.
        }
      }, 500);
    } catch (error) {
      setStatus(error.message || "Camera permission was not granted");
    }
  };

  return (
    <main className="min-h-screen p-5 flex items-center justify-center" style={{ background: "#020202" }}>
      <section className="w-full max-w-[520px] rounded-xl p-6 text-center" style={{ background: "#080704", border: "1px solid rgba(247,201,72,.32)" }}>
        <ScanLine size={36} className="mx-auto mb-3" />
        <h1 className="text-2xl font-bold mb-3">Nexovate Login Scanner</h1>

        {challengeId ? (
          <>
            <CheckCircle2 size={38} className="mx-auto mb-3 text-green-400" />
            <p className="text-sm mb-5">Confirm this one-time desktop login request.</p>
            <button className="btn-solid-green px-5 py-3 rounded" disabled={approving} onClick={approve}>
              {approving ? "Approving..." : "Approve Desktop Login"}
            </button>
          </>
        ) : (
          <>
            <video ref={videoRef} muted playsInline className="w-full rounded-lg mb-4" style={{ minHeight: 220, background: "#000" }} />
            <button className="btn-solid-green px-5 py-3 rounded" onClick={startCamera}>
              <Camera size={17} className="inline mr-2" />Open QR Scanner
            </button>
          </>
        )}

        {status ? <div className="mt-5 text-sm">{status}</div> : null}
      </section>
    </main>
  );
}
